import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { fmt } from "@/lib/pricing";
import { http, STATUS_META, SOURCE_META } from "@/lib/admin";
import { ChevronDown, Trash2, FileText } from "lucide-react";

export function LeadsPanel({ onCreateQuote }) {
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    http.get("/leads").then((r) => setLeads(r.data)).catch(() => toast.error("Nem sikerült betölteni a leadeket"));
  }, []);
  useEffect(load, [load]);

  const updateLead = async (id, patch) => {
    try {
      const { data } = await http.patch(`/leads/${id}`, patch);
      setLeads((ls) => ls.map((l) => (l.id === id ? data : l)));
      toast.success("Mentve");
    } catch {
      toast.error("Sikertelen mentés");
    }
  };

  const removeLead = async (id) => {
    if (!window.confirm("Biztosan törli ezt a leadet?")) return;
    try {
      await http.delete(`/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
    } catch {
      toast.error("Sikertelen törlés");
    }
  };

  const shown = filter === "all" ? leads : leads.filter((l) => l.status === filter);
  const pipeline = leads.filter((l) => l.status !== "lezart").reduce((s, l) => s + l.calculation.total, 0);

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label="Összes lead" value={leads.length} testId="stat-total" />
        <Stat label="Új megkeresés" value={leads.filter((l) => l.status === "uj").length} testId="stat-new" />
        <Stat label="Lezárt üzlet" value={leads.filter((l) => l.status === "lezart").length} testId="stat-closed" />
        <Stat label="Aktív pipeline" value={fmt(pipeline)} testId="stat-pipeline" small />
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {["all", ...Object.keys(STATUS_META)].map((s) => (
          <button key={s} data-testid={`filter-${s}`} onClick={() => setFilter(s)}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold border transition-colors ${
              filter === s ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "bg-white border-[var(--line)] text-[var(--ink-soft)] hover:border-[var(--ink)]"
            }`}>
            {s === "all" ? "Mind" : STATUS_META[s].label}
          </button>
        ))}
      </div>

      <div className="space-y-3" data-testid="lead-list">
        {shown.length === 0 && (
          <p className="text-center text-[var(--ink-muted)] py-16 bg-white rounded-2xl border border-[var(--line)]">
            Nincs megjeleníthető lead.
          </p>
        )}
        {shown.map((l) => (
          <div key={l.id} data-testid={`lead-row-${l.id}`} className="bg-white border border-[var(--line)] rounded-2xl overflow-hidden">
            <button onClick={() => setOpen(open === l.id ? null : l.id)} data-testid={`lead-toggle-${l.id}`}
              className="w-full flex flex-wrap items-center gap-3 px-5 py-4 text-left">
              <span className={`text-[11px] font-semibold border rounded-full px-3 py-1 ${STATUS_META[l.status].cls}`} data-testid={`lead-status-badge-${l.id}`}>
                {STATUS_META[l.status].label}
              </span>
              <span className={`text-[11px] font-semibold border rounded-full px-3 py-1 ${(SOURCE_META[l.source] || SOURCE_META.direct).cls}`} data-testid={`lead-source-badge-${l.id}`}>
                {(SOURCE_META[l.source] || SOURCE_META.direct).label}
              </span>
              <span className="font-semibold text-sm">{l.name}</span>
              <span className="text-xs text-[var(--ink-muted)]">{l.calculation.machine_label} × {l.quantity}</span>
              <span className="ml-auto font-serif-d font-semibold text-[var(--terra)]">{fmt(l.calculation.total)}</span>
              <ChevronDown className={`w-4 h-4 text-[var(--ink-muted)] transition-transform ${open === l.id ? "rotate-180" : ""}`} />
            </button>
            {open === l.id && (
              <div className="border-t border-[var(--line)] px-5 py-5 grid md:grid-cols-2 gap-6 bg-[var(--stone-bg)]">
                <div>
                  <div className="space-y-1.5 text-sm">
                    <Info k="E-mail" v={l.email} />
                    <Info k="Telefon" v={l.phone} />
                    {l.company && <Info k="Stúdió / cég" v={l.company} />}
                    <Info k="Szállítás" v={l.calculation.delivery_label} />
                    <Info k="Fizetés" v={l.calculation.payment_label} />
                    {l.calculation.monthly > 0 && <Info k="Havi törlesztő" v={`${fmt(l.calculation.monthly)} / hó`} />}
                    <Info k="Forrás" v={(SOURCE_META[l.source] || SOURCE_META.direct).label} />
                    <Info k="Beérkezett" v={new Date(l.created_at).toLocaleString("hu-HU")} />
                  </div>
                  <button data-testid={`lead-create-quote-${l.id}`} onClick={() => onCreateQuote(l)}
                    className="mt-5 btn-terra rounded-full px-5 py-2.5 text-xs font-semibold inline-flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5" /> Árajánlat készítése
                  </button>
                </div>
                <div>
                  <p className="eyebrow text-[var(--ink-muted)] mb-2">Státusz</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {Object.entries(STATUS_META).map(([key, m]) => (
                      <button key={key} data-testid={`lead-set-status-${key}-${l.id}`}
                        onClick={() => updateLead(l.id, { status: key })}
                        className={`text-[11px] font-semibold border rounded-full px-3 py-1 transition-opacity ${m.cls} ${l.status === key ? "" : "opacity-40 hover:opacity-100"}`}>
                        {m.label}
                      </button>
                    ))}
                  </div>
                  <p className="eyebrow text-[var(--ink-muted)] mb-2">Jegyzet</p>
                  <NotesEditor lead={l} onSave={(notes) => updateLead(l.id, { notes })} />
                  <button data-testid={`lead-delete-${l.id}`} onClick={() => removeLead(l.id)}
                    className="mt-4 flex items-center gap-1.5 text-xs text-red-600 hover:underline">
                    <Trash2 className="w-3.5 h-3.5" /> Lead törlése
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

export const Stat = ({ label, value, testId, small }) => (
  <div className="bg-white border border-[var(--line)] rounded-2xl p-5" data-testid={testId}>
    <p className="eyebrow text-[var(--ink-muted)]">{label}</p>
    <p className={`font-serif-d font-semibold mt-1.5 ${small ? "text-lg" : "text-3xl"}`}>{value}</p>
  </div>
);

const Info = ({ k, v }) => (
  <p><span className="text-[var(--ink-muted)]">{k}:</span> <span className="font-medium">{v}</span></p>
);

function NotesEditor({ lead, onSave }) {
  const [notes, setNotes] = useState(lead.notes || "");
  return (
    <div>
      <textarea data-testid={`lead-notes-input-${lead.id}`} rows={3} value={notes}
        onChange={(e) => setNotes(e.target.value)} placeholder="Belső jegyzetek a leadhez..."
        className="w-full border border-[var(--line)] rounded-xl px-3.5 py-2.5 text-sm focus:border-[var(--terra)] outline-none bg-white" />
      <button data-testid={`lead-notes-save-${lead.id}`} onClick={() => onSave(notes)}
        className="mt-1.5 text-xs font-semibold text-[var(--terra)] hover:underline">Jegyzet mentése</button>
    </div>
  );
}
